import { useState } from 'react'
import { useAppDispatch } from '../hooks/redux'
import { createProject } from '../store/slices/projectSlice'
import { XMarkIcon } from '@heroicons/react/24/outline'

interface CreateProjectModalProps {
  isOpen: boolean
  onClose: () => void
}

const CreateProjectModal = ({ isOpen, onClose }: CreateProjectModalProps) => {
  const dispatch = useAppDispatch()
  const [formData, setFormData] = useState({
    name: '',
    key: '',
    description: ''
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validateForm = () => {
    const newErrors: Record<string, string> = {}
    
    if (!formData.name.trim()) {
      newErrors.name = '專案名稱不能為空'
    }
    
    if (!formData.key.trim()) {
      newErrors.key = '專案代碼不能為空'
    } else if (!/^[A-Z]{2,10}$/.test(formData.key)) {
      newErrors.key = '專案代碼必須是2-10個大寫英文字母'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setLoading(true)
    try {
      await dispatch(createProject({
        name: formData.name.trim(),
        key: formData.key.trim().toUpperCase(),
        description: formData.description.trim() || undefined
      })).unwrap()
      
      // 成功後重置表單並關閉模態框
      setFormData({ name: '', key: '', description: '' })
      setErrors({})
      onClose()
    } catch (error) {
      console.error('建立專案失敗:', error)
      setErrors({ submit: '建立專案失敗，請稍後再試' })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      setFormData({ name: '', key: '', description: '' })
      setErrors({})
      onClose()
    }
  }

  const handleKeyChange = (value: string) => {
    // 自動轉換為大寫並過濾非字母字符
    const upperKey = value.toUpperCase().replace(/[^A-Z]/g, '')
    setFormData({ ...formData, key: upperKey })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={handleClose} />

        <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              建立新專案
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
              disabled={loading}
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Project Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                專案名稱 *
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="輸入專案名稱"
                disabled={loading}
                autoFocus
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Project Key */}
            <div>
              <label htmlFor="key" className="block text-sm font-medium text-gray-700 mb-1">
                專案代碼 *
              </label>
              <input
                type="text"
                id="key"
                value={formData.key}
                onChange={(e) => handleKeyChange(e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.key ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="例如: PROJ"
                maxLength={10}
                disabled={loading}
              />
              {errors.key && (
                <p className="mt-1 text-sm text-red-600">{errors.key}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                2-10個大寫英文字母，用於生成議題編號（如 {formData.key || 'PROJ'}-1）
              </p>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                專案描述
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="輸入專案描述（選填）"
                disabled={loading}
              />
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{errors.submit}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                disabled={loading}
              >
                取消
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
                disabled={loading || !formData.name.trim() || !formData.key.trim()}
              >
                {loading ? '建立中...' : '建立專案'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default CreateProjectModal